import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ActionDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { ActionRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Action_Bool_Exp,
  GetActionsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetActionsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { ACTION_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  normaliseArrayFilter,
  tagsWhereOnEntity,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

export const mapTrpcActionsToGraphQL = (
  trpcData: ActionRegisterResponse
): StrictMappedGraphQLSelection<GetActionsQuery> => ({
  action: trpcData.action,
});

type InternalArgs = UseGetActionsRegisterArgs & {
  entityIds?: string[];
};

const useActionsGraphqlVariables = (args: InternalArgs) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Action_Bool_Exp>(
    Parent_Type_Enum.Action
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: ACTION_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.actionFilterConditions,
  });

  return { where };
};

const useGetActionsRegisterBase = createQueryHook<
  InternalArgs,
  ActionRegisterResponse,
  GetActionsQuery
>({
  trpcQueryOptions: (
    trpc,
    { parentId, entityIds, tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.action.register.queryOptions({
      parentId,
      entityIds,
      tagTypeIds: normaliseArrayFilter(tagTypeIds),
      departmentTypeIds: normaliseArrayFilter(departmentTypeIds),
      dateRange,
      ownership,
    }),
  mapTrpcDataToGraphQL: mapTrpcActionsToGraphQL,
  graphqlDocument: GetActionsDocument,
  graphqlVariables: useActionsGraphqlVariables,
});

export type UseGetActionsRegisterArgs = {
  parentId?: string;
} & DashboardFilterArgs<ActionDashboardDateField>;

/**
 * Fetches actions with automatic entity filtering for both GraphQL and tRPC
 * paths. Entity filtering uses `useEntityFilter` context internally — consumers
 * don't need to pass entityIds. Dashboard filters (COI-691) are all optional;
 * `tagTypeIds` / `departmentTypeIds` now reach the GraphQL leg too, where they
 * previously only reached tRPC.
 */
export const useGetActionsRegister = (params: {
  queryArgs: UseGetActionsRegisterArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetActionsRegisterBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    suppressErrorNotification: params.suppressErrorNotification,
  });
};
